import React, { useMemo, useState } from 'react';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Alert, Box, Button, Chip, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';

import { formatGstRate } from '~/components/Loyalty/CostPerPoint/Invoicing/invoiceFormat';

import { ROLE_LUX_LOYALTY_ADMIN } from '~/consts/roles';

import { useCppPartners } from '~/hooks/apis/costPerPoint';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { CppPartner } from '~/types/costPerPoint';

import BooleanMark from './BooleanMark';
import CreatePartnerDialog from './CreatePartnerDialog';
import PartnerDetailsDialog from './PartnerDetailsDialog';

/**
 * Ordered list of the six per-partner permission flags and the compact chip
 * label each one renders as. Order here controls the left-to-right chip order.
 */
type PermissionField =
  | 'allowCreditPoints'
  | 'allowCreditStatusCredits'
  | 'allowCreditBonusPoints'
  | 'allowDebitPoints'
  | 'allowDebitStatusCredits'
  | 'allowDebitBonusPoints'
  | 'allowRefund'
  | 'allowTierUpgrade';

const PERMISSION_FLAGS: Array<{ field: PermissionField; label: string }> = [
  { field: 'allowCreditPoints', label: 'Credit Pts' },
  { field: 'allowCreditStatusCredits', label: 'Credit SC' },
  { field: 'allowCreditBonusPoints', label: 'Credit Bonus' },
  { field: 'allowDebitPoints', label: 'Debit Pts' },
  { field: 'allowDebitStatusCredits', label: 'Debit SC' },
  { field: 'allowDebitBonusPoints', label: 'Debit Bonus' },
  { field: 'allowRefund', label: 'Refund' },
  { field: 'allowTierUpgrade', label: 'Tier Upgrade' },
];

/**
 * Renders the enabled-only permission chips for a partner row.
 * Disabled (false) flags are omitted entirely rather than shown as disabled chips;
 * when no flags are enabled a muted em-dash placeholder is shown instead.
 */
function PermissionsCell({ partner }: { partner: CppPartner }) {
  const enabled = PERMISSION_FLAGS.filter(({ field }) => partner[field]);

  if (enabled.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {enabled.map(({ field, label }) => (
        <Chip key={String(field)} size="small" variant="outlined" label={label} />
      ))}
    </Stack>
  );
}

/** A muted dash for a billing field the partner record does not have yet. */
function emptyBillingValue(): React.ReactNode {
  return (
    <Typography variant="body2" color="text.secondary">
      —
    </Typography>
  );
}

/** Builds the grid columns, with the name opening `onOpen` for that row. */
function buildColumns(onOpen: (partner: CppPartner) => void, canEdit: boolean): Array<GridColDef<CppPartner>> {
  return [
    {
      field: 'name',
      headerName: 'Name',
      display: 'flex',
      width: 220,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => onOpen(row)}
            sx={{ textAlign: 'left' }}
          >
            {row.name}
          </Link>
          {/*
            The name alone did not read as something you could open. The icon states it, and says
            which it is: an admin who can change the billing details gets a pencil, everyone else
            gets an eye, because the same dialog is read-only for them.
          */}
          <Tooltip title={canEdit ? 'View and edit partner' : 'View partner'}>
            <IconButton size="small" onClick={() => onOpen(row)} aria-label={canEdit ? 'Edit partner' : 'View partner'}>
              {canEdit ? <EditOutlinedIcon fontSize="inherit" /> : <VisibilityOutlinedIcon fontSize="inherit" />}
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
    {
      field: 'legalName',
      headerName: 'Legal name',
      display: 'flex',
      width: 220,
      valueGetter: (_value, row) => row.legalName ?? '',
      renderCell: ({ row }) => (row.legalName ? row.legalName : emptyBillingValue()),
    },
    {
      field: 'address',
      headerName: 'Billing address',
      display: 'flex',
      flex: 1,
      minWidth: 220,
      sortable: false,
      valueGetter: (_value, row) => row.address ?? '',
      renderCell: ({ row }) =>
        row.address ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', py: 0.5 }}>
            {row.address}
          </Typography>
        ) : (
          emptyBillingValue()
        ),
    },
    {
      field: 'gstPercentage',
      headerName: 'GST',
      display: 'flex',
      width: 90,
      valueGetter: (_value, row) => row.gstPercentage,
      renderCell: ({ row }) =>
        row.gstPercentage == null ? emptyBillingValue() : formatGstRate(row.gstPercentage / 100),
    },
    { field: 'partnerType', headerName: 'Partner Type', display: 'flex', width: 120 },
    { field: 'status', headerName: 'Status', display: 'flex', width: 100 },
    {
      field: 'minPointsPerTransaction',
      headerName: 'Points / Txn',
      display: 'flex',
      width: 140,
      valueGetter: (_value, row) => row.minPointsPerTransaction,
      renderCell: ({ row }) => `${row.minPointsPerTransaction} – ${row.maxPointsPerTransaction}`,
    },
    {
      field: 'minStatusCreditsPerTransaction',
      headerName: 'Status Credits / Txn',
      display: 'flex',
      width: 150,
      valueGetter: (_value, row) => row.minStatusCreditsPerTransaction,
      renderCell: ({ row }) => `${row.minStatusCreditsPerTransaction} – ${row.maxStatusCreditsPerTransaction}`,
    },
    {
      field: 'minBonusPointsPerTransaction',
      headerName: 'Bonus Pts / Txn',
      display: 'flex',
      width: 140,
      valueGetter: (_value, row) => row.minBonusPointsPerTransaction,
      renderCell: ({ row }) => `${row.minBonusPointsPerTransaction} – ${row.maxBonusPointsPerTransaction}`,
    },
    {
      field: 'permissions',
      headerName: 'Permissions',
      display: 'flex',
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: ({ row }) => <PermissionsCell partner={row} />,
    },
    {
      field: 'requireNameMatch',
      headerName: 'Name Match',
      display: 'flex',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_value, row) => row.requireNameMatch,
      renderCell: ({ row }) => <BooleanMark value={row.requireNameMatch} />,
    },
    {
      field: 'failFast',
      headerName: 'Fail Fast',
      display: 'flex',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_value, row) => row.failFast,
      renderCell: ({ row }) => <BooleanMark value={row.failFast} />,
    },
  ];
}

/**
 * Société Point partner directory.
 *
 * Lists every partner registered against the CPP rate-card system. Selecting a name opens the full
 * record, including the billing details the invoice depends on; a loyalty admin can edit those
 * three fields there, anyone else sees the same record read-only. A loyalty admin can also create a
 * partner from here.
 */
export default function PartnersListPage() {
  const { data, isLoading, isError } = useCppPartners();
  const partners: Array<CppPartner> = useMemo(() => data?.result ?? [], [data]);

  const { user } = useCurrentUser();
  const isLoyaltyAdmin = !!user?.roles.includes(ROLE_LUX_LOYALTY_ADMIN);

  const [selected, setSelected] = useState<CppPartner | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const columns = useMemo(() => buildColumns(setSelected, isLoyaltyAdmin), [isLoyaltyAdmin]);

  return (
    <Box sx={{ p: 1 }}>
      {isLoyaltyAdmin && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button variant="contained" onClick={() => setIsCreateOpen(true)}>
            Create partner
          </Button>
        </Stack>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load partners. Please try again.
        </Alert>
      )}

      <Box sx={{ height: 600 }}>
        <DataGridPro
          rows={partners}
          columns={columns}
          loading={isLoading}
          getRowId={(row) => row.id}
          disableRowSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{ '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', py: 1 } }}
        />
      </Box>

      <PartnerDetailsDialog partner={selected} canEdit={isLoyaltyAdmin} onClose={() => setSelected(null)} />
      <CreatePartnerDialog open={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </Box>
  );
}
